import {
  parseISO,
  formatISO,
  format,
  isBefore,
  startOfDay,
  add,
  differenceInDays,
  subDays,
  isAfter,
  isSameDay,
  formatDistanceToNowStrict,
} from 'date-fns'

export const yyyyMMddHHmm = 'yyyy/MM/dd-HH:mm'

export const formatDate = (date: Date, dateFormat = 'dd/MM/yyyy'): string =>
  format(date, dateFormat)

export const formatDateISO = formatISO

export const formatDateISONoTime = (date: Date): string =>
  format(date, "yyyy-MM-dd'T00:00:00Z")

export const utcDateTimeStringToDateString = (date: string): string => date.split('T')[0]

const UNSET_DATE = '0001-01-01'

export const isUnsetDate = (date?: string | null): boolean =>
  !date || utcDateTimeStringToDateString(date) === UNSET_DATE

export const parseDate = (date: string): Date => parseISO(date)

interface ToDateLabelArgs {
  date?: Date
  defaultLabel?: string
  dateFormat?: string
}

export const toDateLabel = ({
  date,
  defaultLabel = 'No date',
  dateFormat = 'dd/MM/yyyy',
}: ToDateLabelArgs): string => (date ? formatDate(date, dateFormat) : defaultLabel)

export type DateRange = {
  startDate: Date
  endDate: Date
}

export const toTodayOrXDaysAgo = (date: Date) =>
  isSameDay(startOfDay(new Date()), startOfDay(date))
    ? 'Today'
    : formatDistanceToNowStrict(date, {
        addSuffix: true,
        unit: 'day',
      })

export const getEarliestDate = (dates: Date[]): Date | undefined =>
  dates.reduce(
    (earliestDate: Date | undefined, date: Date) =>
      earliestDate === undefined || isBefore(date, earliestDate) ? date : earliestDate,
    undefined,
  )

export const getLatestDate = (dates: Date[]): Date | undefined =>
  dates.reduce(
    (latestDate: Date | undefined, date: Date) =>
      latestDate === undefined || isAfter(date, latestDate) ? date : latestDate,
    undefined,
  )

export const to3amSameDay = (date: Date): Date => add(startOfDay(date), { hours: 3 })

export const isAfter3am = (date: Date): boolean => isAfter(date, to3amSameDay(date))

export const rewindTo3am = (date: Date): Date =>
  isAfter3am(date) ? to3amSameDay(date) : subDays(to3amSameDay(date), 1)

export const toEncodedIsoDate = (date: Date): string =>
  encodeURIComponent(formatDateISO(date))

export { differenceInDays }
